import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers-pro';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import {
  SmallButton,
  squareIconButtonSx,
} from '~components/Order/ordersDispatchStyledComponents';
import { FuelPrice } from '~hooks/useFuelPriceIndices/models';
import { useFuelPrices } from '~hooks/useFuelPriceIndices/useFuelPrice';
import useCursorPaginationQuery from '~queries/useCursorPaginationQuery';
import { useStores } from '~store';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

const PAGE_LIMIT = 50;

interface FuelPriceIndexPriceTableProps {
  selectedIndex: string | undefined;
  editable: boolean;
}

type FuelPriceEdit = {
  id: string;
  date: string;
  price: number;
};

type FuelPriceFieldErrors = { date?: string; price?: string };

type FuelPriceRow = FuelPriceEdit & {
  errors: FuelPriceFieldErrors;
  isEditing: boolean;
};

export default function FuelPriceIndexPriceTable({
  selectedIndex,
  editable,
}: FuelPriceIndexPriceTableProps) {
  const {
    getFuelPrices,
    updateFuelPrice,
    deleteFuelPrice,
    createFuelPrice,
    isCreating,
    isUpdating,
    isDeleting,
  } = useFuelPrices();

  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingRow, setEditingRow] = useState<FuelPriceEdit | null>(null);
  const [fieldError, setFieldError] = useState<FuelPriceFieldErrors>({});
  const {
    goToPage,
    isFetching,
    pagination,
    reloadCurrentPage,
    rows: entries,
    startOver,
  } = useCursorPaginationQuery<FuelPrice>({
    fetchPage: (params) => {
      if (!selectedIndex) {
        return Promise.resolve({
          data: [],
          pagination: { limit: params.limit, page: 1 },
        });
      }

      return getFuelPrices({ fuelPriceIndexId: selectedIndex, ...params });
    },
    limit: PAGE_LIMIT,
    queryKey: ['fuelPrices', companyId, selectedIndex],
  });

  const resetEditingState = () => {
    setEditingId(null);
    setEditingRow(null);
    setFieldError({});
    setAdding(false);
  };

  // Ref to always have the latest editingRow
  const editingRowRef = useRef(editingRow);
  useEffect(() => {
    editingRowRef.current = editingRow;
  }, [editingRow]);

  // The row being edited carries its draft and errors, so typing changes only `rows`
  // and the columns, which every cell reads through, stay the same.
  const rows = useMemo<FuelPriceRow[]>(() => {
    const toRow = (edit: FuelPriceEdit, isEditing: boolean): FuelPriceRow => ({
      ...edit,
      errors: isEditing ? fieldError : {},
      isEditing,
    });
    const baseRows = entries
      .map(({ date, id, price }) => ({ date, id, price }))
      .sort((a, b) => b.date.localeCompare(a.date))
      .map((entry) =>
        entry.id === editingId && editingRow
          ? toRow(editingRow, true)
          : toRow(entry, false),
      );
    const isAddingNewRow =
      editingId === 'new' && editingRow && !baseRows.some((row) => row.id === 'new');

    return isAddingNewRow ? [toRow(editingRow, true), ...baseRows] : baseRows;
  }, [entries, editingId, editingRow, fieldError]);

  const isBusy = isCreating || isUpdating || isDeleting;

  const handleEdit = useCallback(
    (id: string) => {
      setEditingId(id);
      if (id === 'new') {
        setEditingRow({ id: 'new', date: '', price: 0 });
      } else {
        const row = entries.find((r) => r.id === id);
        if (row) setEditingRow({ id: row.id, date: row.date, price: row.price });
      }
    },
    [entries],
  );

  const handleSave = useCallback(
    async (id: string) => {
      const row = editingRowRef.current;
      if (!row || !selectedIndex) return;

      const errors: FuelPriceFieldErrors = {};
      if (!row.date) errors.date = 'Date is required';
      if (row.price === undefined || row.price === null)
        errors.price = 'Price is required';
      if (Object.keys(errors).length > 0) {
        setFieldError(errors);
        return;
      }
      setFieldError({});

      try {
        if (id === 'new') {
          await createFuelPrice({
            fuelPriceIndexId: selectedIndex,
            date: row.date,
            price: Number(row.price),
          });
          await startOver();
        } else {
          await updateFuelPrice({
            id,
            date: row.date,
            price: Number(row.price),
          });
          await reloadCurrentPage();
        }
        setEditingId(null);
        setEditingRow(null);
      } catch {
        // Error is already handled by the hook
        setEditingId(null);
        setEditingRow(null);
      } finally {
        setAdding(false);
      }
    },
    [createFuelPrice, updateFuelPrice, reloadCurrentPage, selectedIndex, startOver],
  );

  const handleCancel = useCallback((id: string) => {
    if (id === 'new') {
      setAdding(false);
    }
    setEditingId(null);
    setEditingRow(null);
    setFieldError({});
  }, []);

  const handleDelete = useCallback(
    async (id: string) => {
      if (id === 'new') {
        setAdding(false);
        setEditingRow(null);
        return;
      }
      try {
        await deleteFuelPrice(id);
        await reloadCurrentPage();
      } catch {
        // Error is already handled by the hook
      }
    },
    [deleteFuelPrice, reloadCurrentPage],
  );

  const handlersRef = useRef({ handleCancel, handleDelete, handleEdit, handleSave });
  handlersRef.current = { handleCancel, handleDelete, handleEdit, handleSave };

  const columns = useMemo(() => {
    const dataColumns: Array<DataTableColumn<FuelPriceRow>> = [
      {
        field: 'date',
        headerName: t('administration.fuel_price_index.date'),
        renderCell: (row) => {
          if (row.isEditing) {
            return (
              <DatePicker
                enableAccessibleFieldDOMStructure={false}
                value={dayjs(row.date)}
                format="ddd MMM-DD-YYYY"
                onChange={(newValue) => {
                  setEditingRow((prev) =>
                    prev ? { ...prev, date: newValue?.format('YYYY-MM-DD') ?? '' } : prev,
                  );
                  setFieldError((prev) => ({ ...prev, date: undefined }));
                }}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                    error: !!row.errors.date,
                    disabled: isBusy,
                  },
                }}
              />
            );
          }
          return dayjs(row.date).format('ddd MMM-DD-YYYY');
        },
      },
      {
        field: 'price',
        headerName: t('administration.fuel_price_index.price'),
        renderCell: (row) => {
          if (row.isEditing) {
            return (
              <TextField
                key={`edit-${row.id}-price`}
                type="number"
                value={row.price ?? ''}
                onChange={(e) => {
                  setEditingRow((prev) =>
                    prev ? { ...prev, price: Number(e.target.value) } : prev,
                  );
                  setFieldError((prev) => ({ ...prev, price: undefined }));
                }}
                size="small"
                fullWidth
                slotProps={{
                  htmlInput: { step: '0.001' },
                  input: { startAdornment: '$' },
                }}
                disabled={isBusy}
                error={!!row.errors.price}
              />
            );
          }
          return `$${Number(row.price).toFixed(3)}`;
        },
      },
    ];

    if (!editable) {
      return dataColumns;
    }

    return [
      ...dataColumns,
      actionsColumn<FuelPriceRow>((row) => {
        if (row.isEditing) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmallButton
                aria-label={t('actions.save')}
                color="brandV2Yellow"
                variant="contained"
                onClick={() => handlersRef.current.handleSave(row.id)}
                disabled={isBusy}
                sx={squareIconButtonSx}
              >
                <SaveIcon sx={{ fontSize: 18 }} />
              </SmallButton>
              <SmallButton
                aria-label={t('actions.cancel')}
                color="secondary"
                variant="outlined"
                onClick={() => handlersRef.current.handleCancel(row.id)}
                disabled={isBusy}
                sx={squareIconButtonSx}
              >
                <CancelIcon sx={{ fontSize: 18 }} />
              </SmallButton>
            </Box>
          );
        }
        return (
          <>
            <IconButton
              aria-label={t('actions.edit')}
              disabled={isBusy}
              onClick={() => handlersRef.current.handleEdit(row.id)}
              size="small"
            >
              <EditIcon />
            </IconButton>
            <IconButton
              aria-label={t('actions.delete')}
              disabled={isBusy}
              onClick={() => handlersRef.current.handleDelete(row.id)}
              size="small"
            >
              <DeleteIcon />
            </IconButton>
          </>
        );
      }),
    ];
  }, [editable, isBusy]);

  // Add button handler
  const handleAddClick = () => {
    setAdding(true);
    setEditingId('new');
    setEditingRow({ id: 'new', date: dayjs().format('YYYY-MM-DD'), price: 0 });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mb: 2 }}>
        <DataGridPager
          isFetching={isFetching}
          onNavigate={(link) => {
            resetEditingState();
            void goToPage(link);
          }}
          pagination={pagination}
        />
        {editable && !adding && (
          <SmallButton
            color="brandV2Yellow"
            startIcon={<AddIcon />}
            onClick={handleAddClick}
            disabled={!selectedIndex || isBusy}
          >
            {t('administration.fuel_price_index.add_price')}
          </SmallButton>
        )}
      </Box>
      <DataTable
        id="fuel-price-index-price-table"
        columns={columns}
        rows={rows}
        loading={isFetching || isBusy}
        hideToolbar
        sx={{
          // 362px = the panel's 226px offset + its padding + the index title block +
          // the Add Price row above the table, so the table scrolls inside the panel.
          maxHeight: 'calc(100vh - 362px - var(--tread-agent-dock-height, 0px))',
          overflowY: 'auto',
        }}
      />
    </Box>
  );
}
